import classNames from 'classnames';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StylesheetContext } from '../../../contexts/StylesheetContext';
import { TicketContext } from '../../../contexts/TicketContext';
import { Ticket } from '../../../interfaces/Ticket';
import { DateRange } from '../../../services/ContentfulService';
import { useFormatting } from '../../../services/hooks/useFormatting';
import Calendar from '../Calendar/Calendar';
import { DateBanner } from '../DateBanner/DateBanner';
import { HeaderSvg } from '../HeaderSvg/HeaderSvg';
import style from './ConfirmCard.module.scss';


export interface ConfirmCardProps {
  shouldDisplaySelectedNoTopSlots: boolean;
}

export const ConfirmCard: FC<ConfirmCardProps> = ({ shouldDisplaySelectedNoTopSlots }) => {
  const { t } = useTranslation();
  const { stylesheet } = useContext(StylesheetContext);
  const { ticket } = useContext(TicketContext);

  const appointmentRange: DateRange | undefined = useMemo(() => {
    if (!ticket) {
      return undefined;
    }

    const appointment = ticket.workOrder.appointment;

    if (!appointment.arrivalWindowStart || !appointment.arrivalWindowEnd) {
      return undefined;
    }

    return {
      start: appointment.arrivalWindowStart,
      finish: appointment.arrivalWindowEnd,
    };
  }, [ticket]);

  const { formattedText: appointmentTitle } = useFormatting(t('confirmSection:confirmedAppointment'), {
    replaceDate: appointmentRange?.start,
    range: appointmentRange ? {
      start: appointmentRange.start,
      finish: appointmentRange.finish
    } : undefined,
  });

  const getTitle = useCallback((ticket: Ticket) => {
    if (shouldDisplaySelectedNoTopSlots) {
      return t('confirmSection:selectedNoTopSlotsContentTitle');
    }

    switch (ticket.workOrder.appointment.status) {
      case 'Cannot Complete':
      case 'Completed':
      case 'In Progress':
        return t('confirmSection:expertPassedBy');
      case 'On Route':
        return t('confirmSection:expertOnWay');
      default:
        return appointmentTitle;
    }
  }, [appointmentTitle, shouldDisplaySelectedNoTopSlots, t]);

  const getDescription = useCallback((ticket: Ticket) => {
    if (shouldDisplaySelectedNoTopSlots){
      return t('confirmSection:selectedNoTopSlotsDescription');
    }

    switch (ticket.workOrder.appointment.status){
      case 'Dispatched':
      case 'New':
      case 'On Route':
      case 'Planned':
        return t('confirmSection:confirmedAppointmentDetails');
    }
  }, [shouldDisplaySelectedNoTopSlots, t]);

  if (!ticket){
    return null;
  }

  const { appointment } = ticket.workOrder;

  const description = getDescription(ticket);

  const isCalendarVisible = !!appointmentRange?.start || shouldDisplaySelectedNoTopSlots;

  return (
    <section className={style.confirmCard}>
      <HeaderSvg />
      <div className={style.content}>
        {isCalendarVisible && (
          <DateBanner
            className={style.dateBanner}
            date={appointmentRange?.start}
            icon={shouldDisplaySelectedNoTopSlots ? 'check' : 'calendar'}
          />
        )}
        <h1 className={classNames(style.title, !isCalendarVisible && style.titleCalendarHidden)}>{getTitle(ticket)}</h1>

        {description && <p>{description}</p>}

        {appointment.status === 'Dispatched' && (
          <Calendar appointment={appointment} partnerName={stylesheet.settings.partnerName} text={t('confirmSection:agenda')} />
        )}
      </div>
    </section>
  );
};
